import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { type ColumnDef } from "@tanstack/react-table";
import { Refresh, ToggleLeft, ToggleRight } from "@/components/icons";

import { fetchLogs } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DataTable } from "@/components/ui/data-table";
import { Card, CardContent } from "@/components/ui/card";
import { DataTableShell } from "@/components/ui/data-table-shell";

type ViewMode = "decoded" | "raw";

function formatCommand(entry: any): string {
  const cmd = entry.command_decoded;
  if (cmd && cmd.day) {
    const parts = [
      `Time: ${cmd.day} ${cmd.time || ""}`.trim(),
      `Online: ${cmd.online_current ?? "—"}A`,
      `Offline: ${cmd.offline_current ?? "—"}A`,
      `Rapid: ${cmd.rapid_mode ? "ON" : "OFF"}`,
    ];
    return parts.join(" | ");
  }
  return entry.server_command || entry.command || "—";
}

const decodedColumns: ColumnDef<any, any>[] = [
  {
    accessorKey: "timestamp",
    header: "Timestamp",
    cell: ({ row }) => (
      <span className="font-mono text-xs whitespace-nowrap">
        {row.original.timestamp ? new Date(row.original.timestamp).toLocaleString() : row.original.time || "—"}
      </span>
    ),
  },
  {
    accessorKey: "unit_id",
    header: "Unit ID",
    cell: ({ getValue }) => <span className="font-mono text-xs">{getValue() || "—"}</span>,
  },
  {
    id: "state",
    header: "State",
    cell: ({ row }) => row.original.state_name || row.original.state || "—",
  },
  {
    accessorKey: "voltage",
    header: "Voltage",
    cell: ({ getValue }) => getValue() != null ? `${Number(getValue()).toFixed(1)}V` : "—",
  },
  {
    accessorKey: "current",
    header: "Current",
    cell: ({ getValue }) => getValue() != null ? `${Number(getValue()).toFixed(1)}A` : "—",
  },
  {
    accessorKey: "frequency",
    header: "Freq",
    cell: ({ getValue }) => getValue() != null ? `${Number(getValue()).toFixed(1)}` : "—",
  },
  {
    accessorKey: "power_factor",
    header: "PF",
    cell: ({ getValue }) => getValue() != null ? Number(getValue()).toFixed(2) : "—",
  },
  {
    accessorKey: "session_energy",
    header: "Energy",
    cell: ({ getValue }) => getValue() != null ? `${(Number(getValue()) / 1000).toFixed(2)}` : "—",
  },
  {
    accessorKey: "temperature",
    header: "Temp",
    cell: ({ getValue }) => getValue() != null ? `${getValue()}°C` : "—",
  },
  {
    accessorKey: "wifi_rssi",
    header: "RSSI",
    cell: ({ getValue }) => getValue() != null ? `${getValue()}dBm` : "—",
  },
  {
    id: "command",
    header: "Command",
    cell: ({ row }) => (
      <span className="text-xs max-w-[120px] truncate block">
        {row.original.server_command || row.original.command || "—"}
      </span>
    ),
  },
];

const rawColumns: ColumnDef<any, any>[] = [
  {
    accessorKey: "timestamp",
    header: "Timestamp",
    cell: ({ row }) => (
      <span className="font-mono text-xs whitespace-nowrap">
        {row.original.timestamp ? new Date(row.original.timestamp).toLocaleString() : row.original.time || "—"}
      </span>
    ),
  },
  {
    accessorKey: "unit_id",
    header: "Unit ID",
    cell: ({ getValue }) => <span className="font-mono text-xs">{getValue() || "—"}</span>,
  },
  {
    id: "raw_message",
    header: "Raw Message",
    cell: ({ row }) => (
      <span className="font-mono text-xs max-w-md truncate block">
        {row.original.raw_message || row.original.message || JSON.stringify(row.original)}
      </span>
    ),
  },
  {
    id: "command",
    header: "Command",
    cell: ({ row }) => (
      <span className="text-xs">{row.original.server_command || row.original.command || "—"}</span>
    ),
  },
];

export function LogsPage() {
  // Deep-link support: /logs?unit_id=XXX pre-fills the filter (used by the
  // "Logs" button on the unit detail page). The input stays editable — the
  // param only seeds the initial value.
  const [searchParams] = useSearchParams();
  const [unitId, setUnitId] = useState(() => searchParams.get("unit_id") ?? "");
  const [limit, setLimit] = useState(500);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [viewMode, setViewMode] = useState<ViewMode>("decoded");

  const q = useQuery({
    queryKey: ["logs", { unitId, limit }],
    queryFn: () => fetchLogs({ unit_id: unitId || undefined, limit }),
    refetchInterval: autoRefresh ? 5_000 : false,
  });

  const entries: any[] = Array.isArray(q.data) ? q.data : (q.data as any)?.logs ?? [];
  const isStale = !!(q.data && !Array.isArray(q.data) && (q.data as any).stale);
  const staleReason = isStale ? (q.data as any).reason : null;

  return (
    <div className="space-y-5 pt-[86px]">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Logs</h1>
          <div className="text-sm opacity-70">Server telemetry log stream</div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setAutoRefresh(!autoRefresh)}
            title={autoRefresh ? "Pause auto-refresh" : "Enable auto-refresh"}
          >
            {autoRefresh ? <ToggleRight className="h-4 w-4 text-success" /> : <ToggleLeft className="h-4 w-4" />}
            {autoRefresh ? "Auto: ON" : "Auto: OFF"}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => q.refetch()}>
            <Refresh className="h-4 w-4" /> Refresh
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="py-4">
          <div className="flex flex-wrap items-end gap-4">
            <div className="min-w-[200px]">
              <Label>Unit ID (optional)</Label>
              <Input className="mt-1" placeholder="SIM001..." value={unitId} onChange={(e) => setUnitId(e.target.value)} />
            </div>
            <div className="w-32">
              <Label>Limit</Label>
              <Input className="mt-1" type="number" value={String(limit)} onChange={(e) => setLimit(Number(e.target.value) || 500)} />
            </div>
            <ToggleGroup type="single" size="sm" variant="outline" value={viewMode} onValueChange={(v) => v && setViewMode(v as ViewMode)}>
              <ToggleGroupItem value="decoded">Decoded</ToggleGroupItem>
              <ToggleGroupItem value="raw">Raw</ToggleGroupItem>
            </ToggleGroup>
          </div>
        </CardContent>
      </Card>

      {isStale && (
        <div className="rounded-md border border-warning/50 bg-warning/10 px-3 py-2 text-sm text-warning-content">
          Diagnostics unavailable — UDP listener may be down or recently restarted
          {staleReason ? ` (${staleReason})` : ""}.
        </div>
      )}

      {/* Content */}
      {q.isError ? (
        <div className="rounded-md border border-destructive/50 bg-destructive/10 p-4 text-destructive">Failed to load logs</div>
      ) : (
        <DataTableShell>
          <DataTable
            columns={viewMode === "decoded" ? decodedColumns : rawColumns}
            data={entries}
            isLoading={q.isLoading}
            tableClassName="text-xs"
            emptyMessage="No log entries"
          />
        </DataTableShell>
      )}

      <div className="text-center text-xs opacity-50">
        {entries.length} entries • {autoRefresh ? "Auto-refreshing every 5s" : "Auto-refresh paused"}
      </div>
    </div>
  );
}
